/**
 * Enhanced Toast Manager Utility
 *
 * Provides centralized toast management with:
 * - Deduplication: Prevents identical toasts
 * - Grouping: Combines similar toasts
 * - Rate Limiting: Prevents toast spam
 * - Priority System: Important toasts shown first
 * - Smart Auto-Dismiss: Based on severity
 *
 * Based on UX research from Binance, Coinbase, Kraken, Bybit, OKX
 */

import { toast } from 'react-toastify';
import i18next from 'i18next';

class ToastManager {
  constructor() {
    this.activeToasts = new Set();
    this.toastQueue = [];
    this.maxActiveToasts = 3; // Maximum number of toasts to show at once (industry standard)
    this.deduplicationWindow = 3000; // 3 seconds - ignore identical messages
    this.groupingWindow = 10000; // 10 seconds - group similar messages
    this.maxToastsPerMinute = 10; // Rate limiting
    this.lastToastTimes = new Map(); // Track when each message was last shown
    this.groupedToasts = new Map(); // Track grouped messages
    this.toastHistory = []; // Track toast history for rate limiting

    // Priority levels (higher = more important)
    this.priorities = {
      critical: 5,  // Connection lost, Security alerts
      error: 4,     // API errors, Order failures
      warning: 3,   // Rate limits, Low balance
      success: 2,   // Order filled, Deposits
      info: 1       // General updates
    };

    // Auto-dismiss timing based on severity (ms)
    this.autoDismissTiming = {
      critical: 0,      // Manual dismiss only
      error: 10000,     // 10 seconds
      warning: 8000,    // 8 seconds
      success: 4000,    // 4 seconds
      info: 5000        // 5 seconds
    };
  }

  /**
   * Generate a unique key for a toast message
   */
  generateToastKey(message, type = 'default') {
    // Extract text content if message is JSX
    const messageText = typeof message === 'string'
      ? message
      : this.extractTextFromJSX(message);
    return `${type}:${messageText}`;
  }

  /**
   * Extract text from JSX for grouping
   */
  extractTextFromJSX(jsx) {
    if (typeof jsx === 'string') return jsx;
    if (!jsx) return '';

    // Handle React elements
    if (jsx.props && jsx.props.children) {
      const children = jsx.props.children;
      if (Array.isArray(children)) {
        return children.map(child => this.extractTextFromJSX(child)).join(' ');
      }
      return this.extractTextFromJSX(children);
    }

    return String(jsx);
  }

  /**
   * Check if rate limit is exceeded
   */
  isRateLimited() {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    // Clean old history
    this.toastHistory = this.toastHistory.filter(time => time > oneMinuteAgo);

    // Check if we've exceeded the limit
    if (this.toastHistory.length >= this.maxToastsPerMinute) {
      console.warn('🚫 Toast rate limit exceeded. Suppressing toast.');
      return true;
    }

    return false;
  }

  /**
   * Check if a toast should be shown based on deduplication rules
   */
  shouldShowToast(key, priority = 2) {
    const now = Date.now();
    const lastTime = this.lastToastTimes.get(key);

    // Check rate limiting (skip for critical messages)
    if (priority < this.priorities.critical && this.isRateLimited()) {
      return false;
    }

    // If this exact message was shown recently, try to group it
    if (lastTime && (now - lastTime) < this.deduplicationWindow) {
      this.tryGroupToast(key);
      return false;
    }

    // If we have too many active toasts, queue it
    if (this.activeToasts.size >= this.maxActiveToasts) {
      return false;
    }

    return true;
  }

  /**
   * Try to group a toast with existing similar toast
   */
  tryGroupToast(key) {
    const grouped = this.groupedToasts.get(key);
    if (grouped) {
      grouped.count++;
      grouped.lastTime = Date.now();

      // Update the toast with new count
      if (grouped.toastId) {
        toast.update(grouped.toastId, {
          render: this.createGroupedMessage(grouped.originalMessage, grouped.count),
        });
      }
    } else {
      // Start a new group
      this.groupedToasts.set(key, {
        count: 2, // Current + this one
        lastTime: Date.now(),
        originalMessage: key.split(':')[1], // Extract message from key
        toastId: null
      });
    }
  }

  /**
   * Create a grouped message with counter
   */
  createGroupedMessage(message, count) {
    if (typeof message === 'string') {
      return `${message} (×${count})`;
    }

    // For JSX messages, wrap with counter
    return (
      <div>
        {message}
        <div style={{ fontSize: '0.85em', opacity: 0.8, marginTop: '4px' }}>
          {count} similar notifications
        </div>
      </div>
    );
  }

  /**
   * Get priority level from type
   */
  getPriority(type, options = {}) {
    // Allow explicit priority override
    if (options.priority !== undefined) {
      return options.priority;
    }

    // Map type to priority
    switch (type) {
      case 'critical':
        return this.priorities.critical;
      case 'error':
        return this.priorities.error;
      case 'warning':
        return this.priorities.warning;
      case 'success':
        return this.priorities.success;
      case 'info':
      default:
        return this.priorities.info;
    }
  }

  /**
   * Get auto-dismiss timing based on type
   */
  getAutoDismissTiming(type, options = {}) {
    // Allow explicit autoClose override
    if (options.autoClose !== undefined) {
      return options.autoClose;
    }

    return this.autoDismissTiming[type] || this.autoDismissTiming.info;
  }

  /**
   * Show a toast with deduplication, grouping, and rate limiting
   */
  showToast(message, type = 'default', options = {}) {
    if (!message) return;

    const key = this.generateToastKey(message, type);
    const priority = this.getPriority(type, options);

    if (!this.shouldShowToast(key, priority)) {
      // Add to queue if not already there (priority queue)
      if (!this.toastQueue.some(item => item.key === key)) {
        this.toastQueue.push({ key, message, type, options, priority });
        // Sort queue by priority (highest first)
        this.toastQueue.sort((a, b) => b.priority - a.priority);
      }
      return;
    }

    // Add to history for rate limiting
    this.toastHistory.push(Date.now());

    // Set auto-dismiss timing based on type
    const autoClose = this.getAutoDismissTiming(type, options);
    const enhancedOptions = {
      ...options,
      autoClose
    };

    // Show the toast
    const toastId = this.displayToast(message, type, enhancedOptions);

    if (toastId) {
      this.activeToasts.add(key);
      this.lastToastTimes.set(key, Date.now());

      // Track grouped toast ID
      const grouped = this.groupedToasts.get(key);
      if (grouped) {
        grouped.toastId = toastId;
      }

      // Set up cleanup when toast is dismissed
      const originalOnClose = enhancedOptions.onClose;
      enhancedOptions.onClose = () => {
        this.activeToasts.delete(key);
        this.groupedToasts.delete(key);
        this.processQueue();
        if (originalOnClose) originalOnClose();
      };
    }

    return toastId;
  }

  /**
   * Actually display the toast using react-toastify
   */
  displayToast(message, type, options) {
    const defaultOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    };

    // Map 'critical' to 'error' for display
    const displayType = type === 'critical' ? 'error' : type;

    switch (displayType) {
      case 'success':
        return toast.success(message, defaultOptions);
      case 'error':
        return toast.error(message, defaultOptions);
      case 'warning':
        return toast.warning(message, defaultOptions);
      case 'info':
        return toast.info(message, defaultOptions);
      default:
        return toast(message, defaultOptions);
    }
  }

  /**
   * Process queued toasts when space becomes available
   */
  processQueue() {
    if (this.toastQueue.length === 0) return;
    
    // Process up to the available slots
    const availableSlots = this.maxActiveToasts - this.activeToasts.size;
    const toastsToShow = this.toastQueue.splice(0, availableSlots);
    
    toastsToShow.forEach(({ key, message, type, options }) => {
      if (this.shouldShowToast(key)) {
        this.showToast(message, type, options);
      }
    });
  }

  /**
   * Show error toast with user-friendly message
   */
  showError(error, defaultMessage = null) {
    console.log("🚨 ToastManager.showError called with:", error);
    console.log("🚨 ToastManager.showError - error type:", typeof error);
    console.log("🚨 ToastManager.showError - error message:", error?.message);
    console.log("🚨 ToastManager.showError - error response:", error?.response?.data);

    let message = defaultMessage;

    // Extract user-friendly message from error
    // Priority: 1. String error, 2. Server message, 3. Generic error message
    if (typeof error === 'string') {
      message = error;
    } else if (error?.response?.data?.message) {
      // ✅ HIGHEST PRIORITY: Server message from API response
      message = error.response.data.message;
      console.log("🚨 ToastManager.showError - using server message:", message);
    } else if (error?.message) {
      // Lower priority: Generic axios error message
      message = this.getUserFriendlyErrorMessage(error.message);
      console.log("🚨 ToastManager.showError - using generic error message:", message);
    }

    // Fallback to default message based on language
    if (!message) {
      message = i18next.language === 'ir' || i18next.language === 'fa'
        ? 'خطایی رخ داده است. لطفاً دوباره تلاش کنید.'
        : 'An error occurred. Please try again.';
    }

    console.log("🚨 ToastManager.showError - final message:", message);
    this.showToast(message, 'error');
  }

  /**
   * Convert technical error messages to user-friendly ones
   */
  getUserFriendlyErrorMessage(technicalMessage) {
    if (!technicalMessage) return null;
    
    const message = technicalMessage.toLowerCase();
    const isIranian = i18next.language === 'ir' || i18next.language === 'fa';
    
    // Socket connection errors
    if (message.includes('socket') || message.includes('connection refused') || message.includes('fwrite')) {
      return isIranian 
        ? 'مشکلی در برقراری ارتباط رخ داده است. لطفاً دوباره تلاش کنید.'
        : 'Connection problem occurred. Please try again.';
    }
    
    // Network errors
    if (message.includes('network') || message.includes('timeout') || message.includes('unreachable')) {
      return isIranian
        ? 'مشکلی در شبکه رخ داده است. اتصال اینترنت خود را بررسی کنید.'
        : 'Network problem occurred. Please check your internet connection.';
    }
    
    // Server errors
    if (message.includes('server') || message.includes('internal') || message.includes('500')) {
      return isIranian
        ? 'مشکلی در سرور رخ داده است. لطفاً چند دقیقه دیگر تلاش کنید.'
        : 'Server problem occurred. Please try again in a few minutes.';
    }
    
    // Authentication errors
    if (message.includes('unauthorized') || message.includes('401') || message.includes('forbidden')) {
      return isIranian
        ? 'دسترسی شما منقضی شده است. لطفاً دوباره وارد شوید.'
        : 'Your session has expired. Please log in again.';
    }
    
    // Validation errors
    if (message.includes('validation') || message.includes('invalid') || message.includes('required')) {
      return isIranian
        ? 'اطلاعات وارد شده صحیح نیست. لطفاً بررسی کنید.'
        : 'The information entered is incorrect. Please check and try again.';
    }
    
    // Return original message if no pattern matches, but clean it up
    return technicalMessage.charAt(0).toUpperCase() + technicalMessage.slice(1);
  }

  /**
   * Clear all active toasts
   */
  clearAll() {
    toast.dismiss();
    this.activeToasts.clear();
    this.toastQueue = [];
    this.lastToastTimes.clear();
    this.groupedToasts.clear();
    this.toastHistory = [];
  }

  /**
   * Show success message
   */
  showSuccess(message, options = {}) {
    this.showToast(message, 'success', options);
  }

  /**
   * Show warning message
   */
  showWarning(message, options = {}) {
    this.showToast(message, 'warning', options);
  }

  /**
   * Show info message
   */
  showInfo(message, options = {}) {
    this.showToast(message, 'info', options);
  }

  /**
   * Show critical message (manual dismiss only)
   */
  showCritical(message, options = {}) {
    this.showToast(message, 'critical', options);
  }

  /**
   * Show grouped notification (for bulk operations)
   *
   * @param {Array} items - Array of items to group
   * @param {string} singularMessage - Message for single item (e.g., "Order filled")
   * @param {string} pluralMessage - Message for multiple items (e.g., "orders filled")
   * @param {string} type - Toast type (success, error, etc.)
   * @param {Object} options - Additional options
   */
  showGrouped(items, singularMessage, pluralMessage, type = 'success', options = {}) {
    if (!items || items.length === 0) return;

    const count = items.length;
    let message;

    if (count === 1) {
      message = singularMessage;
    } else {
      message = `${count} ${pluralMessage}`;
    }

    // Add click handler to show details if provided
    const enhancedOptions = {
      ...options,
      onClick: options.onShowDetails ? () => options.onShowDetails(items) : options.onClick
    };

    this.showToast(message, type, enhancedOptions);
  }

  /**
   * Show connection error with retry button
   */
  showConnectionError(retryCallback, options = {}) {
    const lang = i18next.language;
    const message = lang === 'fa' || lang === 'ir'
      ? 'مشکل در اتصال به سرور'
      : 'Connection issues detected';

    const enhancedOptions = {
      ...options,
      autoClose: false, // Don't auto-dismiss connection errors
      onClick: retryCallback
    };

    this.showToast(
      <div>
        <div>{message}</div>
        {retryCallback && (
          <button
            onClick={retryCallback}
            style={{
              marginTop: '8px',
              padding: '4px 12px',
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {lang === 'fa' || lang === 'ir' ? 'تلاش مجدد' : 'Retry'}
          </button>
        )}
      </div>,
      'critical',
      enhancedOptions
    );
  }

  /**
   * Get statistics about toast manager
   */
  getStats() {
    return {
      activeToasts: this.activeToasts.size,
      queuedToasts: this.toastQueue.length,
      groupedToasts: this.groupedToasts.size,
      toastsLastMinute: this.toastHistory.length,
      rateLimitActive: this.isRateLimited()
    };
  }
}

// Create singleton instance
const toastManager = new ToastManager();

export default toastManager;

// Export convenience methods
export const showToast = (message, type, options) => toastManager.showToast(message, type, options);
export const showError = (error, defaultMessage) => toastManager.showError(error, defaultMessage);
export const showSuccess = (message, options) => toastManager.showSuccess(message, options);
export const showWarning = (message, options) => toastManager.showWarning(message, options);
export const showInfo = (message, options) => toastManager.showInfo(message, options);
export const showCritical = (message, options) => toastManager.showCritical(message, options);
export const showGrouped = (items, singular, plural, type, options) =>
  toastManager.showGrouped(items, singular, plural, type, options);
export const showConnectionError = (retryCallback, options) =>
  toastManager.showConnectionError(retryCallback, options);
export const clearAllToasts = () => toastManager.clearAll();
export const getToastStats = () => toastManager.getStats();
